import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import ModalFade, {
  useModalFade,
} from '../../Microreels/List/components/ModalFade';
import PostPhoneScreen from './PostPhoneScreen';
import {PostMediaItem} from '../../../data/models/Posts/Post';
import {postsText} from '../postsText';
import styles from './PostPreviewOverlay.module.css';

interface Props {
  shown: boolean;
  items: PostMediaItem[];
  title: string;
  caption?: string;
  onClose: () => void;
}

const PostPreviewOverlay = React.memo(
  ({shown, items, title, caption, onClose}: Props) => {
    const [index, setIndex] = useState(0);
    const fade = useModalFade(shown);

    const lastIndex = items.length - 1;

    useEffect(() => {
      if (shown) setIndex(0);
    }, [shown]);

    useEffect(() => {
      if (!shown) return;
      const onKey = (ev: KeyboardEvent) => {
        if (ev.key === 'Escape') onClose();
        if (ev.key === 'ArrowLeft')
          setIndex(current => Math.max(0, current - 1));
        if (ev.key === 'ArrowRight')
          setIndex(current => Math.min(lastIndex, current + 1));
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [shown, lastIndex, onClose]);

    // The overlay is portaled to document.body, so the whole app root (top bar
    // and side menu included) can be blurred behind it.
    useEffect(() => {
      const root = document.getElementById('root');
      if (!root) return;
      root.style.transition = 'filter 0.2s ease';
      root.style.filter = shown ? 'blur(6px)' : '';
      return () => {
        root.style.filter = '';
        root.style.transition = '';
      };
    }, [shown]);

    if (!fade.rendered) return null;

    const goTo = (next: number) => {
      if (next < 0 || next > lastIndex) return;
      setIndex(next);
    };

    return createPortal(
      <ModalFade closing={fade.closing}>
        <div className={styles.overlay} onClick={onClose}>
          <button
            className={styles.close}
            onClick={onClose}
            aria-label={postsText.exitPreview}>
            ✕
          </button>
          <div
            className={styles.phoneWrap}
            onClick={ev => ev.stopPropagation()}>
            <div className={styles.phone}>
              <PostPhoneScreen
                items={items}
                title={title}
                caption={caption}
                index={index}
                onIndexChange={goTo}
              />
            </div>
            {items.length > 1 && (
              <div className={styles.navRow}>
                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() => goTo(index - 1)}
                  disabled={index === 0}
                  aria-label={postsText.moveLeft}>
                  <span className={`icon-chevron-right ${styles.flipped}`} />
                </button>
                <div className={styles.dots}>
                  {items.map((item, itemIndex) => (
                    <button
                      key={item.id}
                      className={`${styles.dot} ${
                        itemIndex === index ? styles.dotActive : ''
                      }`}
                      onClick={() => goTo(itemIndex)}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() => goTo(index + 1)}
                  disabled={index === lastIndex}
                  aria-label={postsText.moveRight}>
                  <span className="icon-chevron-right" />
                </button>
              </div>
            )}
            <button className={styles.exitButton} onClick={onClose}>
              {postsText.exitPreview}
            </button>
          </div>
        </div>
      </ModalFade>,
      document.body,
    );
  },
);

export default PostPreviewOverlay;
